import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  cancelInterview,
  // Changing status is switched off for now; see ADMIN_MOVES below.
  // changeApplicationStatus,
  withdrawOffer,
} from '@/api/adminRecruitment.api';
import {
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  CardActions,
  EmptyState,
  ErrorState,
  Icon,
  ListCard,
  LoadingState,
  Modal,
  PageHeader,
  Pagination,
  SearchInput,
  SegmentedTabs,
  Select,
  Textarea,
  ViewButton,
  useToast,
} from '@/components/ui';
import { isCandidateRestricted } from '@/features/applications/accountStanding';
import { CandidateRestrictionBadge } from '@/features/applications/CandidateRestriction';
import { APPLICATION_LABELS, APPLICATION_TONES } from '@/features/applications/status';
import {
  INTERVIEW_LABELS,
  INTERVIEW_TONES,
  MODE_LABELS,
  RESPONSE_LABELS,
  RESPONSE_TONES,
  interviewWhen,
} from '@/features/interviews/status';
import { OFFER_LABELS, OFFER_TONES, stipendLine } from '@/features/offers/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/format';

/**
 * What is actually happening between companies and students.
 *
 * Analytics counts this; the internship record shows one listing's share of
 * it; the recruiter record shows one person's. None of them answers "what is
 * happening on the platform this week?", which is the question behind a funnel
 * that narrows sharply at one stage, a company that never replies, or a
 * recruiter booking interviews nobody accepts.
 *
 * Three tabs rather than three pages because they are three views of one
 * pipeline: an application becomes an interview becomes an offer, and an admin
 * following a complaint moves between them without changing subject.
 *
 * The day-to-day decisions stay with the company. The row actions are for
 * when the platform has to step in -- a candidate stranded by a company that
 * went quiet, a round booked in bad faith, an offer that should not stand --
 * and each makes exactly the move the company could make, with the same
 * history and notifications, attributed to the platform.
 */
const TABS = [
  { key: 'applications', label: 'Applications' },
  { key: 'interviews', label: 'Interviews' },
  { key: 'offers', label: 'Offers' },
];

/** How far back to look. Recent is the usual question; all-time is the escape. */
const WINDOWS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '', label: 'All time' },
];

const PAGE_SIZE = 20;

export function AdminRecruitmentPage() {
  // Tab and scope live in the URL so a link from a company or student record
  // arrives on the right list, and survives a reload.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const tab = TABS.some((entry) => entry.key === requested) ? requested : 'applications';
  const scope = {
    ...(searchParams.get('company_id') ? { company_id: searchParams.get('company_id') } : {}),
    ...(searchParams.get('student_id') ? { student_id: searchParams.get('student_id') } : {}),
    ...(searchParams.get('internship_id')
      ? { internship_id: searchParams.get('internship_id') }
      : {}),
  };
  const scoped = Object.keys(scope).length > 0;

  function selectTab(key) {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
  }

  return (
    <div>
      <PageHeader
        title="Applications"
        description="Every application, interview and offer on the platform, newest first."
      />

      <SegmentedTabs
        tabs={TABS}
        value={tab}
        onChange={(key) => selectTab(key)}
        label="Recruitment stage"
        className="mb-6"
      />

      {scoped && (
        <p className="text-ink-muted mb-6 text-sm">
          Narrowed to one {scope.company_id ? 'company' : scope.student_id ? 'student' : 'listing'}.{' '}
          <button
            type="button"
            className="text-brand-700 hover:text-brand-800 font-medium underline-offset-2 hover:underline focus-visible:rounded"
            onClick={() => setSearchParams({ tab }, { replace: true })}
          >
            Show the whole platform
          </button>
        </p>
      )}

      {/* Keyed so switching tabs starts its filters clean rather than carrying
          a status from a list where it meant something else. */}
      {tab === 'applications' && <ApplicationList key="applications" scope={scope} />}
      {tab === 'interviews' && <InterviewList key="interviews" scope={scope} />}
      {tab === 'offers' && <OfferList key="offers" scope={scope} />}

      <p className="text-ink-muted mt-6 text-xs">
        Metadata only: cover letters, internal notes, joining links, interviewer feedback and the
        text of an offer are not shown here and are not reachable from here.
      </p>
    </div>
  );
}

/**
 * The parts every tab repeats: the query, the empty and error states, and the
 * pagination. Each list supplies its own filters and its own boxes, because
 * that is all they genuinely differ in.
 */
function useRecruitmentList(key, fetcher, params) {
  return useQuery({
    queryKey: [`admin-recruitment-${key}`, params],
    queryFn: () => fetcher(params),
    // Without this every keystroke in the search box unmounts the results for
    // the loading state, taking the focused input with them.
    placeholderData: keepPreviousData,
  });
}

function Results({ query, filters, activeCount, onReset, children }) {
  const { data, isPending, isPlaceholderData, isError, error, refetch } = query;
  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <ListCard
      isRefreshing={isPlaceholderData}
      toolbarEnd={
        <>
          {filters}
          {activeCount > 0 && (
            <Button variant="ghost" size="sm" onClick={onReset}>
              <Icon name="reset" size={14} />
              Reset
            </Button>
          )}
        </>
      }
      state={
        isError ? (
          <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
        ) : isPending ? (
          <LoadingState label="Loading…" />
        ) : rows.length === 0 ? (
          activeCount > 0 ? (
            <EmptyState
              title="Nothing matches these filters."
              description="Widen the window or clear a filter to see more."
              action={
                <Button variant="secondary" size="sm" onClick={onReset}>
                  Reset filters
                </Button>
              }
            />
          ) : (
            <EmptyState title="Nothing here" description="No activity on the platform yet." />
          )
        ) : null
      }
      footer={
        meta && (
          <Pagination
            className=""
            page={meta.page}
            pageSize={PAGE_SIZE}
            totalPages={meta.total_pages}
            totalItems={meta.total_items}
            onPageChange={query.onPageChange}
          />
        )
      }
    >
      <BoxList>{children(rows)}</BoxList>
    </ListCard>
  );
}

/**
 * One box: the candidate, and the account behind them, since every tab is a
 * list of people in somebody's pipeline. Each tab adds its own badges and chips.
 * The box opens the application it belongs to; the student is one click on.
 *
 * A candidate whose account is suspended, banned or deactivated is marked the
 * way the company sees them -- faded, with a danger stripe and badge -- but
 * the interventions stay: an admin can still reject, cancel or withdraw.
 */
function RecordBox({
  row,
  accent,
  badges,
  facts,
  description,
  action,
  corner,
  verb = 'Applied for',
}) {
  // Interview and offer rows name their application; an application row is one.
  const applicationId = row.application_id ?? row.id;
  const restricted = isCandidateRestricted(row);
  return (
    <BoxRow
      muted={restricted}
      accent={restricted ? 'danger' : (accent ?? 'neutral')}
      leading={<BoxAvatar name={row.student_name} round />}
      title={row.student_name}
      to={paths.admin.applicationDetail(applicationId)}
      badges={
        <>
          {badges}
          <CandidateRestrictionBadge row={row} />
        </>
      }
      // What the row is about, said in a sentence: the listing and the
      // company it belongs to, both one click away.
      subtitle={
        <span className="text-ink-muted">
          {verb}{' '}
          <Link
            to={paths.admin.internshipDetail(row.internship_id)}
            onClick={(event) => event.stopPropagation()}
            className="text-ink hover:text-brand-700 relative font-semibold hover:underline"
          >
            {row.internship_title}
          </Link>{' '}
          at{' '}
          <Link
            to={paths.admin.companyDetail(row.company_id)}
            onClick={(event) => event.stopPropagation()}
            className="text-ink hover:text-brand-700 relative font-semibold hover:underline"
          >
            {row.company_name}
          </Link>
        </span>
      }
      facts={[{ icon: 'user', text: row.student_email }, ...facts]}
      description={description}
      actions={
        <CardActions>
          {action}
          <ViewButton
            to={paths.admin.applicationDetail(applicationId)}
            title={`Open ${row.student_name}'s application`}
            label="View application"
          />
        </CardActions>
      }
      // Interventions that end something (cancel a round, withdraw an offer)
      // sit in the corner, apart from the everyday footer.
      corner={corner}
    />
  );
}

/**
 * Every intervention can move rows on all three tabs (a rejection cancels the
 * round and pulls the offer), so all three lists refetch.
 */
function useInterventionRefresh() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith('admin-recruitment-'),
    });
    void queryClient.invalidateQueries({ queryKey: ['admin-overview'] });
  };
}

/** Asks for the one thing a cancellation or withdrawal needs: a reason. */
function ReasonModal({ open, title, description, confirmLabel, isLoading, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const text = reason.trim();

  return (
    <Modal open={open} title={title} description={description} onClose={onClose}>
      <div className="space-y-4">
        <Textarea
          label="Reason"
          required
          rows={4}
          hint="The candidate and the company both see this."
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={text.length < 4}
            isLoading={isLoading}
            onClick={() => onConfirm(text, () => setReason(''))}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// --------------------------------------------------------------------------- //
// Applications
// --------------------------------------------------------------------------- //
/**
 * Statuses where "days in stage" is a stall rather than a fact. Once somebody
 * is hired, rejected or gone, time in that state means nothing.
 */
const LIVE_STAGES = new Set([
  'SUBMITTED',
  'UNDER_REVIEW',
  'SHORTLISTED',
  'INTERVIEW',
  'DECISION_PENDING',
  'OFFERED',
  'OFFER_ACCEPTED',
]);

/*
 * Changing an application's status from the console is switched off for now.
 * To bring it back, uncomment this block, the `changing` state, the "Change
 * status" button and the modal in ApplicationList, and the
 * `changeApplicationStatus` import. The endpoint is still on the backend.
 *
 * The moves a company may make by hand, mirrored from the backend's
 * COMPANY_STATUS_CHANGES. Interview, offer and acceptance are reached through
 * their own actions, so they never appear here.
 *
const ADMIN_MOVES = {
  SUBMITTED: ['UNDER_REVIEW', 'SHORTLISTED', 'REJECTED'],
  UNDER_REVIEW: ['SHORTLISTED', 'REJECTED'],
  SHORTLISTED: ['REJECTED'],
  INTERVIEW: ['REJECTED'],
  DECISION_PENDING: ['REJECTED'],
  OFFERED: ['REJECTED'],
  OFFER_ACCEPTED: ['HIRED', 'REJECTED'],
};

function ChangeStatusModal({ application, onClose }) {
  const toast = useToast();
  const refresh = useInterventionRefresh();
  const moves = ADMIN_MOVES[application?.status] ?? [];
  const [target, setTarget] = useState(moves[0] ?? '');
  const [rejectionReason, setRejectionReason] = useState('');
  const [note, setNote] = useState('');
  const rejecting = target === 'REJECTED';

  const change = useMutation({
    mutationFn: () =>
      changeApplicationStatus(application.id, {
        status: target,
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(rejecting && rejectionReason.trim()
          ? { rejection_reason: rejectionReason.trim() }
          : {}),
      }),
    onSuccess: () => {
      toast.success(`Moved to ${APPLICATION_LABELS[target] ?? target}`);
      refresh();
      onClose();
    },
    onError: (changeError) => toast.error(errorMessage(changeError, 'Could not change the status')),
  });

  return (
    <Modal
      open={Boolean(application)}
      title={`Change status for ${application?.student_name}`}
      description="The candidate and the company team are notified, and the history records it as an admin action. A rejection also cancels booked interviews and withdraws open offers."
      onClose={onClose}
    >
      <div className="space-y-4">
        <Select
          label="Move to"
          required
          value={target}
          onChange={(event) => setTarget(event.target.value)}
        >
          {moves.map((value) => (
            <option key={value} value={value}>
              {APPLICATION_LABELS[value] ?? value}
            </option>
          ))}
        </Select>
        {rejecting && (
          <Textarea
            label="Rejection reason"
            rows={3}
            maxLength={500}
            hint="Shown to the candidate."
            value={rejectionReason}
            onChange={(event) => setRejectionReason(event.target.value)}
          />
        )}
        <Textarea
          label="Note"
          rows={3}
          maxLength={480}
          hint="Kept on the application history, not shown to the candidate."
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={rejecting ? 'danger' : 'primary'}
            disabled={!target}
            isLoading={change.isPending}
            onClick={() => change.mutate()}
          >
            Change status
          </Button>
        </div>
      </div>
    </Modal>
  );
}
*/

function ApplicationList({ scope }) {
  // Changing status is switched off for now; see ADMIN_MOVES above.
  // const [changing, setChanging] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [days, setDays] = useState('30');
  const [page, setPage] = useState(1);

  const params = {
    page,
    page_size: PAGE_SIZE,
    ...scope,
    ...(search.trim() ? { q: search.trim() } : {}),
    ...(status ? { statuses: status } : {}),
    ...(days ? { days: Number(days) } : {}),
  };
  const query = useRecruitmentList('applications', adminApi.listApplications, params);

  const activeCount = [search !== '', status !== '', days !== '30'].filter(Boolean).length;

  function resetFilters() {
    setSearch('');
    setStatus('');
    setDays('30');
    setPage(1);
  }

  function change(setter) {
    return (event) => {
      setter(event.target.value);
      setPage(1);
    };
  }

  return (
    <>
      <Results
        query={{ ...query, onPageChange: setPage }}
        activeCount={activeCount}
        filters={
          <>
            <Select
              label="Stage"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={status}
              onChange={change(setStatus)}
            >
              <option value="">Any stage</option>
              {Object.entries(APPLICATION_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Select
              label="Applied"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={days}
              onChange={change(setDays)}
            >
              {WINDOWS.map((window) => (
                <option key={window.label} value={window.value}>
                  {`Applied: ${window.label}`}
                </option>
              ))}
            </Select>
            <SearchInput
              label="Search applications"
              hideLabel
              size="sm"
              className="w-full sm:w-64"
              placeholder="Candidate or company"
              value={search}
              onChange={change(setSearch)}
              onClear={() => {
                setSearch('');
                setPage(1);
              }}
            />
          </>
        }
        onReset={resetFilters}
      >
        {(rows) =>
          rows.map((row) => (
            <RecordBox
              key={row.id}
              row={row}
              accent={APPLICATION_TONES[row.status]}
              badges={
                <Badge dot tone={APPLICATION_TONES[row.status] ?? 'neutral'}>
                  {APPLICATION_LABELS[row.status] ?? row.status}
                </Badge>
              }
              facts={[
                // Days since the last move, which is what makes a company
                // sitting on candidates visible without opening it.
                LIVE_STAGES.has(row.status) &&
                  row.days_in_stage > 0 && {
                    icon: 'clockOutline',
                    text: `${row.days_in_stage} ${row.days_in_stage === 1 ? 'day' : 'days'} in this stage`,
                  },
                // Frozen at the moment of applying, not recomputed since.
                {
                  icon: 'target',
                  text:
                    row.match_score == null
                      ? 'No match score'
                      : `${Math.round(row.match_score)}% match`,
                },
                { icon: 'calendar', text: `Applied ${formatDate(row.created_at)}` },
                row.decided_at && { icon: 'check', text: `Decided ${formatDate(row.decided_at)}` },
              ]}
              description={row.rejection_reason}
              /* Changing status is switched off for now; see ADMIN_MOVES above.
              action={
                ADMIN_MOVES[row.status] && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setChanging(row)}
                    title={`Change the status of the application by ${row.student_name}`}
                  >
                    <Icon name="pencil" size={14} />
                    Change status
                  </Button>
                )
              } */
            />
          ))
        }
      </Results>
      {/* Changing status is switched off for now; see ADMIN_MOVES above.
          Keyed by row so the form opens on that application's own moves.
      {changing && (
        <ChangeStatusModal
          key={changing.id}
          application={changing}
          onClose={() => setChanging(null)}
        />
      )} */}
    </>
  );
}

// --------------------------------------------------------------------------- //
// Interviews
// --------------------------------------------------------------------------- //
function InterviewList({ scope }) {
  const toast = useToast();
  const refresh = useInterventionRefresh();
  const [cancelling, setCancelling] = useState(null);
  const cancel = useMutation({
    mutationFn: ({ id, reason }) => cancelInterview(id, reason),
    onSuccess: () => {
      toast.success('Interview cancelled');
      refresh();
      setCancelling(null);
    },
    onError: (cancelError) =>
      toast.error(errorMessage(cancelError, 'Could not cancel the interview')),
  });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [mode, setMode] = useState('');
  const [when, setWhen] = useState('');
  const [days, setDays] = useState('');
  const [page, setPage] = useState(1);

  const params = {
    page,
    page_size: PAGE_SIZE,
    ...scope,
    ...(search.trim() ? { q: search.trim() } : {}),
    ...(status ? { statuses: status } : {}),
    ...(mode ? { modes: mode } : {}),
    // Two different questions: `upcoming` is when the conversation happens,
    // `days` is when somebody booked it.
    ...(when ? { upcoming: when === 'upcoming' } : {}),
    ...(days ? { days: Number(days) } : {}),
  };
  const query = useRecruitmentList('interviews', adminApi.listInterviews, params);

  const activeCount = [search !== '', status !== '', mode !== '', when !== '', days !== ''].filter(
    Boolean,
  ).length;

  function resetFilters() {
    setSearch('');
    setStatus('');
    setMode('');
    setWhen('');
    setDays('');
    setPage(1);
  }

  function change(setter) {
    return (event) => {
      setter(event.target.value);
      setPage(1);
    };
  }

  return (
    <>
      <Results
        query={{ ...query, onPageChange: setPage }}
        activeCount={activeCount}
        filters={
          <>
            <Select
              label="Slot"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={when}
              onChange={change(setWhen)}
            >
              <option value="">Any date</option>
              <option value="upcoming">Still to happen</option>
              <option value="past">Already passed</option>
            </Select>
            <Select
              label="Status"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={status}
              onChange={change(setStatus)}
            >
              <option value="">Any status</option>
              {Object.entries(INTERVIEW_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Select
              label="Mode"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={mode}
              onChange={change(setMode)}
            >
              <option value="">Any mode</option>
              {Object.entries(MODE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Select
              label="Booked"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={days}
              onChange={change(setDays)}
            >
              {WINDOWS.map((window) => (
                <option key={window.label} value={window.value}>
                  {`Booked: ${window.label}`}
                </option>
              ))}
            </Select>
            <SearchInput
              label="Search interviews"
              hideLabel
              size="sm"
              className="w-full sm:w-64"
              placeholder="Candidate or company"
              value={search}
              onChange={change(setSearch)}
              onClear={() => {
                setSearch('');
                setPage(1);
              }}
            />
          </>
        }
        onReset={resetFilters}
      >
        {(rows) =>
          rows.map((row) => (
            <RecordBox
              key={row.id}
              row={row}
              verb="Interview for"
              accent={INTERVIEW_TONES[row.status]}
              badges={
                <>
                  <Badge dot tone={INTERVIEW_TONES[row.status] ?? 'neutral'}>
                    {INTERVIEW_LABELS[row.status] ?? row.status}
                  </Badge>
                  {/* A run of PENDING against one company is the pattern this
                      list exists to make visible. */}
                  <Badge tone={RESPONSE_TONES[row.student_response] ?? 'neutral'}>
                    {RESPONSE_LABELS[row.student_response] ?? row.student_response}
                  </Badge>
                </>
              }
              facts={[
                { icon: 'calendarClock', tone: 'brand', text: interviewWhen(row) },
                {
                  icon: 'chat',
                  text: `Round ${row.round_number} · ${MODE_LABELS[row.mode] ?? row.mode}`,
                },
                row.reschedule_count > 0 && {
                  icon: 'repeat',
                  text: `Moved ${row.reschedule_count} ${row.reschedule_count === 1 ? 'time' : 'times'}`,
                },
                // Null once the seat-holder's account is gone; the interview
                // outlives them and the row still has to say something.
                {
                  icon: 'userCheck',
                  text: `Booked by ${row.scheduled_by_name ?? 'a removed account'}`,
                },
              ]}
              description={row.cancellation_reason}
              corner={
                row.status === 'SCHEDULED' && (
                  <Button
                    variant="ghostDanger"
                    size="sm"
                    onClick={() => setCancelling(row)}
                    aria-label={`Cancel round ${row.round_number} with ${row.student_name}`}
                    title={`Cancel round ${row.round_number} with ${row.student_name}`}
                  >
                    <Icon name="close" size={14} />
                    Cancel round
                  </Button>
                )
              }
            />
          ))
        }
      </Results>
      <ReasonModal
        open={Boolean(cancelling)}
        title={`Cancel round ${cancelling?.round_number} with ${cancelling?.student_name}`}
        description="The candidate and the company team are notified, and the application steps back to where it was before the round was booked."
        confirmLabel="Cancel round"
        isLoading={cancel.isPending}
        onClose={() => setCancelling(null)}
        onConfirm={(reason, clear) =>
          cancel.mutate({ id: cancelling.id, reason }, { onSuccess: clear })
        }
      />
    </>
  );
}

// --------------------------------------------------------------------------- //
// Offers
// --------------------------------------------------------------------------- //
/**
 * `responded_at` is stamped by whoever ended the offer -- the candidate
 * answering it, or the company withdrawing it. Only the first is an answer,
 * and calling a withdrawal one would put words in the candidate's mouth.
 */
const ANSWERED = new Set(['ACCEPTED', 'DECLINED']);

/** Offers that are still open, and so can be pulled. */
const WITHDRAWABLE = new Set(['DRAFT', 'SENT']);

function OfferList({ scope }) {
  const toast = useToast();
  const refresh = useInterventionRefresh();
  const [withdrawing, setWithdrawing] = useState(null);
  const withdraw = useMutation({
    mutationFn: ({ id, reason }) => withdrawOffer(id, reason),
    onSuccess: () => {
      toast.success('Offer withdrawn');
      refresh();
      setWithdrawing(null);
    },
    onError: (withdrawError) =>
      toast.error(errorMessage(withdrawError, 'Could not withdraw the offer')),
  });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [days, setDays] = useState('');
  const [page, setPage] = useState(1);

  const params = {
    page,
    page_size: PAGE_SIZE,
    ...scope,
    ...(search.trim() ? { q: search.trim() } : {}),
    ...(status ? { statuses: status } : {}),
    ...(days ? { days: Number(days) } : {}),
  };
  const query = useRecruitmentList('offers', adminApi.listOffers, params);

  const activeCount = [search !== '', status !== '', days !== ''].filter(Boolean).length;

  function resetFilters() {
    setSearch('');
    setStatus('');
    setDays('');
    setPage(1);
  }

  function change(setter) {
    return (event) => {
      setter(event.target.value);
      setPage(1);
    };
  }

  return (
    <>
      <Results
        query={{ ...query, onPageChange: setPage }}
        activeCount={activeCount}
        filters={
          <>
            <Select
              label="Status"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={status}
              onChange={change(setStatus)}
            >
              <option value="">Any status</option>
              {Object.entries(OFFER_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <Select
              label="Made"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-44"
              value={days}
              onChange={change(setDays)}
            >
              {WINDOWS.map((window) => (
                <option key={window.label} value={window.value}>
                  {`Made: ${window.label}`}
                </option>
              ))}
            </Select>
            <SearchInput
              label="Search offers"
              hideLabel
              size="sm"
              className="w-full sm:w-64"
              placeholder="Candidate or company"
              value={search}
              onChange={change(setSearch)}
              onClear={() => {
                setSearch('');
                setPage(1);
              }}
            />
          </>
        }
        onReset={resetFilters}
      >
        {(rows) =>
          rows.map((row) => (
            <RecordBox
              key={row.id}
              row={row}
              verb="Offer for"
              accent={OFFER_TONES[row.status]}
              badges={
                <Badge dot tone={OFFER_TONES[row.status] ?? 'neutral'}>
                  {OFFER_LABELS[row.status] ?? row.status}
                </Badge>
              }
              facts={[
                { icon: 'banknote', tone: 'brand', text: stipendLine(row) },
                { icon: 'calendar', text: `Starts ${formatDate(row.starts_on)}` },
                // A draft exists but the candidate has never seen it, which is
                // the difference between an offer made and one written.
                {
                  icon: 'send',
                  text: row.sent_at ? `Sent ${formatDate(row.sent_at)}` : 'Never sent',
                },
                { icon: 'clockOutline', text: `Answer by ${formatDate(row.respond_by)}` },
                row.responded_at && {
                  icon: 'check',
                  text: `${ANSWERED.has(row.status) ? 'Answered' : 'Closed'} ${formatDate(row.responded_at)}`,
                },
                { icon: 'userCheck', text: `Made by ${row.made_by_name ?? 'a removed account'}` },
              ]}
              corner={
                WITHDRAWABLE.has(row.status) && (
                  <Button
                    variant="ghostDanger"
                    size="sm"
                    onClick={() => setWithdrawing(row)}
                    title={`Withdraw the offer to ${row.student_name}`}
                  >
                    <Icon name="close" size={14} />
                    Withdraw offer
                  </Button>
                )
              }
            />
          ))
        }
      </Results>
      <ReasonModal
        open={Boolean(withdrawing)}
        title={`Withdraw the offer to ${withdrawing?.student_name}`}
        description="The company team is notified, and so is the candidate if the offer was sent. A sent offer hands the application back to the company to decide."
        confirmLabel="Withdraw offer"
        isLoading={withdraw.isPending}
        onClose={() => setWithdrawing(null)}
        onConfirm={(reason, clear) =>
          withdraw.mutate({ id: withdrawing.id, reason }, { onSuccess: clear })
        }
      />
    </>
  );
}
